'use client';

import {
  SyntheticEvent, useEffect, useRef, useState,
} from 'react';
import classNames from 'classnames';
import { RoadmapForm } from '@sapientpro/sapientpro-data-models';
import { useAtomValue } from 'jotai';
// eslint-disable-next-line import/no-extraneous-dependencies
import parsePhoneNumberFromString from 'libphonenumber-js';
import styles from './SurveySection.module.scss';
import Button, { ButtonVariants } from '../Button';
import SurveyQuestion, { TSurveyAnswer, TSurveyQuestion } from './SurveyQuestion';
import apiClient from '../../apiClient';
import useDebouncedCallback from '../../useDebounceCallback';
import homeSurveyFormData from '../../store/HomeSurveyForm';

type TContactType = 'email' | 'phoneNumber' | 'whatsApp' | null;

export type TSelectedAnswer = {
  questionKey: TSurveyQuestion['analyticsValue'];
  answers: {
    analyticsValue: TSurveyAnswer['analyticsValue'];
    slug: string;
    type: string;
  }[];
};
interface ISurveyBlockProps {
  questions: RoadmapForm['tabs'];
  formView?: boolean;
  handleAnswersChange?: (selectedAnswers: TSelectedAnswer[]) => void;
}

type TSubmitResponse = {
  message: string;
  error?: {
    message: string;
  }
};

const getContactPlaceholder = (type: TContactType, onlyEmail: boolean) => {
  switch (type) {
    case 'email': {
      return 'Enter your email';
    }
    case 'phoneNumber': {
      return 'Enter your number';
    }
    default: {
      const placeholder = onlyEmail ? 'Enter your email' : 'Enter your number or email';
      return placeholder;
    }
  }
};

const SurveyBlock = ({ questions, formView, handleAnswersChange }: ISurveyBlockProps) => {
  const { isHomeSurveyContactButtonClicked } = useAtomValue(homeSurveyFormData);
  const [activeQuestion, setActiveQuestion] = useState<number>(questions?.[0]?.id || 0);
  const [selectedAnswers, setSelectedAnswers] = useState<TSelectedAnswer[]>([]);
  const [isFormValid, setIsFormValid] = useState<boolean>(true);
  const [formMessage, setFormMessage] = useState<string>('');
  const [requestError, setRequestError] = useState<string>('');
  const [userEmail, setUserEmail] = useState<string>('');
  const [contactType, setContactType] = useState<TContactType>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const fieldTypeRef = useRef<'email' | 'phoneNumber' | 'whatsApp' | null>(formView ? 'email' : null);
  const selectedAnswersRef = useRef<TSelectedAnswer[]>([]);

  const debounceClearError = useDebouncedCallback(() => setRequestError(''), 5000);

  const saveSelectedOptions = async ({ answers, contact }: { answers: TSelectedAnswer[], contact?: string }) => {
    try {
      let bodyData: {
        selectedOptions?: { [key: string]: string }[],
        email?: string,
        phoneNumber?: string,
        kind: 'hero' | 'slider',
        ctaClicked: boolean,
      } = {
        kind: formView ? 'hero' : 'slider',
        ctaClicked: isHomeSurveyContactButtonClicked,
      };
      const selectedOptions = answers.reduce((result, currentItem) => ({
        ...result,
        [currentItem.questionKey]: currentItem?.answers[0]?.analyticsValue,
      }), [] as { [key: string]: string }[]);

      if (Object.entries(selectedOptions).length) {
        bodyData.selectedOptions = selectedOptions;
      }

      if (contact) {
        const isNumber = /^\+?\s?(\d+|\(\d+\))[\d\s]*$/.test(contact);
        bodyData[isNumber ? 'phoneNumber' : 'email'] = contact;
      }

      if (Object.entries(bodyData).length) {
        const body = JSON.stringify(bodyData);
        await apiClient.post('roadmap-form-result', { body });
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleNextQuestion = () => {
    const nextQuestionIndex = questions.findIndex(question => question.id === activeQuestion) + 1;
    const isLast = questions.length === nextQuestionIndex;

    if (nextQuestionIndex) {
      if (!isLast) {
        const questionId = questions[nextQuestionIndex].id;
        setActiveQuestion(questionId);
      } else {
        setActiveQuestion(questions.length);
      }
    }
  };

  const checkMailAddressValidity = (address: string) => {
    let validationError = '';
    const phoneNumber = parsePhoneNumberFromString(address);
    const isNumber = !!phoneNumber?.isValid();
    const isValidEmail = /\S+@\S+\.\S+/.test(address);
    const onlyEmail = !formView;
    const type = fieldTypeRef.current;

    if (address !== '') {
      if (!type && (!isNumber || onlyEmail) && !isValidEmail) {
        const errorText = onlyEmail ? 'Please enter a valid email' : 'Please enter a valid email or phone number';
        validationError = errorText;
      } else if (['phoneNumber', 'whatsApp'].includes(type || '') && !isNumber) {
        validationError = 'Please enter a valid phone number';
      } else if (type === 'email' && !isValidEmail) {
        validationError = 'Please enter a valid email';
      }
    } else {
      validationError = 'Please provide your contacts';
    }

    setIsFormValid(!validationError);
    setFormMessage(validationError);
  };

  const handleAnswerChange = (answer: TSurveyAnswer, questionKey: TSurveyQuestion['analyticsValue']) => {
    const prevAnswers = selectedAnswersRef.current;
    const savedQuestion = prevAnswers.find((currentItem) => questionKey === currentItem.questionKey);

    if (questionKey === 'Share the best way to contact you') {
      const newType = answer.analyticsValue === 'Email' ? 'email' : (
        answer.analyticsValue === 'Phone Number' ? 'phoneNumber' : 'whatsApp');

      const newValue = newType === contactType ? null : newType;

      setContactType(newValue);
      fieldTypeRef.current = newValue;

      if (userEmail) {
        checkMailAddressValidity(userEmail);
      }
    }

    const answerItems = answer?.content?.reduce((result, currentItem) => {
      if (!currentItem.service && !currentItem.subservice) return result;

      const type = currentItem.service ? 'service' : 'subservice';
      const slug = currentItem[type]?.slug as string;

      return [...result, {
        type, slug, analyticsValue: answer.analyticsValue,
      }];
    }, [] as TSelectedAnswer['answers']) || [];

    if (!savedQuestion) {
      const answers = answerItems.length ? answerItems : [{ analyticsValue: answer.analyticsValue, slug: '', type: '' }];
      const newEntry = {
        questionKey,
        answers,
      };

      handleAnswersChange?.([...prevAnswers, newEntry]);
      setSelectedAnswers([...prevAnswers, newEntry]);
      selectedAnswersRef.current = [...prevAnswers, newEntry];
      handleNextQuestion();
    } else {
      const newSelectedAnswers = prevAnswers.filter((item) => item.questionKey !== savedQuestion.questionKey);
      const isSelected = savedQuestion.answers.find((currentItem) => currentItem.analyticsValue === answer.analyticsValue);
      const newAnswers = [...answerItems];

      const newSelectedAnswer = {
        questionKey: savedQuestion.questionKey,
        answers: newAnswers,
      };

      if (!isSelected) {
        handleAnswersChange?.([...prevAnswers, newSelectedAnswer]);
        setSelectedAnswers([...prevAnswers, newSelectedAnswer]);
        handleNextQuestion();
      } else {
        handleAnswersChange?.(newSelectedAnswers);
        setSelectedAnswers(newSelectedAnswers);
      }
    }
  };

  const handleActiveChange = (questionId: number) => {
    const isActive = activeQuestion === questionId;

    if (isActive) setActiveQuestion(0);
    else setActiveQuestion(questionId);
  };

  const handleSubmit = async (event: SyntheticEvent) => {
    event.preventDefault();

    try {
      const isPhoneNumber = ['whatsApp', 'phoneNumber'].includes(fieldTypeRef.current || '');
      const type = isPhoneNumber ? 'phoneNumber' : 'email';
      const body = JSON.stringify({ [type]: userEmail });
      const { response, status } = await apiClient.postResponse<TSubmitResponse>('contact-form-lead/roadmap-form', { body });

      saveSelectedOptions({ answers: selectedAnswersRef.current, contact: userEmail });

      if (status === 200) {
        formRef.current?.reset();
        setUserEmail('');
        setFormMessage('The form has been successfully submitted');
      } else {
        setRequestError(response.message || response?.error?.message || '');
        debounceClearError();
      }
    } catch (error) {
      console.error(error);
      setFormMessage(error);
    }
  };

  const handleChange = (value: string) => {
    setIsFormValid(true);
    setUserEmail(value);
    checkMailAddressValidity(value);
  };

  const runSelectedValuesSave = useDebouncedCallback(() => saveSelectedOptions({ answers: selectedAnswersRef.current }), 500);

  useEffect(() => {
    runSelectedValuesSave();
  }, [selectedAnswers.length, isHomeSurveyContactButtonClicked]);

  const inputIndex = questions.length;
  const isContactNumber = contactType === 'phoneNumber';

  return (
    <div className={classNames(styles.surveyBlock, {
      [styles.formView]: formView,
    })}
    >
      {questions.map((question) => (
        <SurveyQuestion
          key={question.id}
          question={question}
          handleActiveChange={() => setActiveQuestion(question.id)}
          handleAnswerChange={handleAnswerChange}
          isActive={question.id === activeQuestion}
          formView={formView}
        />
      ))}
      <div className={classNames(styles.surveyQuestion, {
        [styles.active]: activeQuestion === inputIndex,
      })}
      >
        <button
          type='button'
          className={styles.question}
          onClick={() => handleActiveChange(inputIndex)}
        >
          <p className={classNames(styles.questionText, 'header3')}>Leave your contact</p>
          <svg className={styles.questionIcon}>
            <use
              xlinkHref='/media/icon-nest.svg#iconNest'
              href='/media/icon-nest.svg#iconNest'
            />
          </svg>
        </button>
        <form
          onSubmit={handleSubmit}
          className={classNames(styles.answerOptions, styles.inputWrapper, {
            [styles.visible]: formView,
          })}
          ref={formRef}
        >
          <div className={styles.contactInputWrapper}>
            <input
              name='email'
              type='text'
              placeholder={getContactPlaceholder(contactType, !formView)}
              className={classNames(styles.contactInput)}
              value={userEmail}
              onChange={e => handleChange(e.target.value)}
            />
            {isContactNumber && <p className={styles.phoneNumberInfo}>Include country code, e.g. +1 202 565 1234</p>}
          </div>
          <Button
            variant={ButtonVariants.PRIMARY}
            className={styles.submitButton}
            disabled={!isFormValid || !userEmail}
            type='submit'
          >
            Send
          </Button>
          <p className={classNames(styles.status, {
            [styles.error]: !isFormValid || !!requestError,
            [styles.success]: isFormValid && formMessage,
            [styles.forPhoneNumber]: isContactNumber,
          })}
          >
            {formMessage || requestError}
          </p>
        </form>
      </div>
      <svg className={styles.sectionDecoration}>
        <use
          href='media/Home/Hero/hero_ellipse.svg#homeHeroEllipseSVG'
          xlinkHref='media/Home/Hero/hero_ellipse.svg#homeHeroEllipseSVG'
        />
      </svg>
    </div>
  );
};

export default SurveyBlock;
